/* eslint-disable default-case, no-param-reassign, consistent-return, prefer-destructuring */
/*
 *
 * One Time Payment reducer
 *
 */

import produce, { original } from 'immer';

import { RESET_SWITCH_LOAN_STATE, RESET_USER_STATE } from '../../Main/constants';
import {
  CANCEL_OTP,
  CANCEL_OTP_FAILURE,
  CANCEL_OTP_SUCCESS,
  CHANGE_INPUT,
  CHANGE_SCHEDULED_DATE,
  CHANGE_SCHEDULED_DATE_ON_DECK,
  CHANGE_STEP,
  CREATE_OTP,
  CREATE_OTP_FAILURE,
  CREATE_OTP_SUCCESS,
  CREATE_OTP_VALIDATION_FAILURE,
  FETCH_PAYMENT_HOME,
  FETCH_PAYMENT_HOME_FAILURE,
  FETCH_PAYMENT_HOME_SUCCESS,
  NACHA_SAVE_TOKEN,
  RESET_BANNER_ERROR,
  RESET_CANCEL_CONFIRMATION,
  RESET_CONFIRMATION,
  RESET_ONE_TIME_PAYMENT_STATE,
  RESET_STEP,
  SET_BANNER_ERROR,
  SET_IS_AMOUNT_DRAWER_OPEN,
  SET_IS_MODAL_OPEN,
  UPDATE_AMOUNT,
  UPDATE_PAYMENT_HOME_INIT,
} from './constants';

export const initialState = {
  errors: {
    bannerError: false,
    serviceError: {
      createOtp: false,
      otp: false,
      paymentHome: false,
    },
  },
  isPaymentHomeInit: false,
  loading: {
    createOtp: false,
    otp: false,
  },
  nachaToken: '',
  otp: {
    accountType: 'Checking',
    allowedOperation: '',
    availableDraftDates: [],
    availableNumberOfPayments: [],
    cancelConfirmation: false,
    confirmation: false,
    dateAfterGracePeriod: '',
    fields: [],
    formErrors: {
      amount: {
        additionalEscrow: '',
        additionalPrincipal: '',
        lateCharge: '',
        nsfFees: '',
        numberOfPayments: '',
        otherFees: '',
      },
    },
    forms: {
      account: { scheduledDate: '' },
      amount: {
        additionalEscrow: 0,
        additionalPrincipal: 0,
        lateCharge: 0,
        nsfFees: 0,
        numberOfPayments: 1,
        otherFees: 0,
      },
      review: { isLatePaymentConfirmChecked: false },
    },
    isAfterGracePeriod: false,
    isAmountDrawerOpen: false,
    isModalOpen: {
      cancel: false,
      latePaymentWarning: false,
    },
    nsfFees: 0,
    otherFees: 0,
    outstandingLateCharges: 0,
    paymentLogic: {},
    payType: '',
    pendingPaymentTotal: 0,
    pendingScheduledDate: '',
    regularMonthlyPayment: 0,
    scheduledDate: '',
    scheduledDateOnDeck: '', // onDeck = pending user's choice in LatePaymentWarning modal
    status: '',
    totalPaymentDue: 0,
    useExistingAccountInformation: false,
  },
  requestStatus: { paymentHome: 'idle' },
  step: 1,
  successes: false,
};

const oneTimePaymentReducer = produce((draft, { payload, type }) => {
  switch (type) {
    case CANCEL_OTP: {
      draft.errors = initialState.errors;
      draft.otp.cancelConfirmation = {
        amount: draft.otp.pendingPaymentTotal,
        date: draft.otp.pendingScheduledDate,
      };
      draft.loading.otp = true;
      break;
    }
    case CANCEL_OTP_FAILURE: {
      draft.loading.otp = false;
      draft.errors.serviceError.otp = true;
      draft.otp.cancelConfirmation = initialState.otp.cancelConfirmation;
      break;
    }
    case CANCEL_OTP_SUCCESS: {
      const { cancelConfirmation: confirmationNumber } = payload;
      draft.otp.cancelConfirmation.confirmationNumber = confirmationNumber;
      draft.loading.otp = false;
      draft.requestStatus.paymentHome = 'idle';
      draft.otp.confirmation = initialState.otp.confirmation;
      break;
    }
    case CHANGE_INPUT: {
      const { field, form, value } = payload;
      draft.otp.forms[form][field] = value;
      break;
    }
    case CHANGE_SCHEDULED_DATE: {
      const { shouldClearOnDeck, value } = payload;
      draft.otp.scheduledDate = value;
      if (shouldClearOnDeck) {
        draft.otp.scheduledDateOnDeck = '';
      }
      break;
    }
    case CHANGE_SCHEDULED_DATE_ON_DECK: {
      const { value } = payload;
      draft.otp.scheduledDateOnDeck = value;
      break;
    }
    case CHANGE_STEP: {
      const { step } = payload;
      draft.step = step;
      break;
    }
    case CREATE_OTP: {
      draft.errors = initialState.errors;
      draft.loading.createOtp = true;
      break;
    }
    case CREATE_OTP_FAILURE: {
      draft.errors.serviceError.createOtp = true;
      draft.loading.createOtp = false;
      break;
    }
    case CREATE_OTP_SUCCESS: {
      const {
        confirmationNumber,
        numberOfPayments,
        scheduledDate,
        scheduledTotalPayment,
      } = payload;
      draft.loading.createOtp = false;
      draft.otp.confirmation = {
        confirmationNumber,
        numberOfPayments,
        scheduledDate,
        scheduledTotalPayment,
      };
      draft.otp.pendingPaymentTotal = scheduledTotalPayment;
      draft.otp.pendingScheduledDate = scheduledDate;
      draft.requestStatus.paymentHome = 'idle';
      break;
    }
    case CREATE_OTP_VALIDATION_FAILURE: {
      const { amount, bannerError, screen } = payload;
      draft.errors.bannerError = bannerError;
      draft.loading.createOtp = false;
      Object.keys(amount).forEach((key) => {
        draft.otp.formErrors.amount[key] = amount[key];
      });
      const hasErrors = bannerError
        || (Object.values(draft.otp.formErrors.amount).reduce((acc, value) => acc || !!value));
      if (hasErrors && screen === 'EditPayment') {
        draft.otp.isAmountDrawerOpen = true;
      }
      break;
    }
    case FETCH_PAYMENT_HOME: {
      draft.errors.serviceError.paymentHome = initialState.errors.serviceError.paymentHome;
      draft.isInPaymentFlow = true;
      draft.requestStatus.paymentHome = 'loading';
      break;
    }
    case FETCH_PAYMENT_HOME_FAILURE: {
      draft.errors.serviceError.paymentHome = true;
      draft.requestStatus.paymentHome = 'failed';
      break;
    }
    case FETCH_PAYMENT_HOME_SUCCESS: {
      const {
        allowedOperation,
        amount: {
          availableNumberOfPayments = [],
          fields = [],
          formErrors = initialState.otp.formErrors.amount,
          forms = initialState.otp.forms.amount,
        },
        availableDraftDates,
        dateAfterGracePeriod,
        isAfterGracePeriod,
        maximumAllowedAdditionalPrincipal,
        maximumAllowedTotalPayment,
        nsfFees,
        otherFees,
        outstandingLateCharges,
        paymentLogic,
        payType,
        pendingPaymentTotal,
        pendingScheduledDate,
        regularMonthlyPayment,
        status,
        totalPaymentDue,
      } = payload;
      draft.requestStatus.paymentHome = 'succeeded';
      draft.otp.allowedOperation = allowedOperation;
      draft.otp.availableNumberOfPayments = availableNumberOfPayments;
      draft.otp.availableDraftDates = availableDraftDates || [];
      draft.otp.fields = fields;
      draft.otp.dateAfterGracePeriod = dateAfterGracePeriod;
      draft.otp.isAfterGracePeriod = isAfterGracePeriod;
      draft.otp.formErrors.amount = formErrors;
      draft.otp.forms.amount = forms;
      draft.otp.maximumAllowedAdditionalPrincipal = maximumAllowedAdditionalPrincipal;
      draft.otp.maximumAllowedTotalPayment = maximumAllowedTotalPayment;
      draft.otp.nsfFees = nsfFees;
      draft.otp.otherFees = otherFees;
      draft.otp.outstandingLateCharges = outstandingLateCharges;
      draft.otp.paymentLogic = paymentLogic;
      draft.otp.payType = payType || '';
      draft.otp.pendingPaymentTotal = pendingPaymentTotal || 0;
      draft.otp.pendingScheduledDate = pendingScheduledDate || '';
      draft.otp.regularMonthlyPayment = regularMonthlyPayment;
      draft.otp.status = status;
      draft.otp.totalPaymentDue = totalPaymentDue;
      draft.otp.scheduledDate =
        availableDraftDates && availableDraftDates[0] ? availableDraftDates[0] : '';
      break;
    }
    case NACHA_SAVE_TOKEN: {
      const { token } = payload;
      draft.nachaToken = token;
      break;
    }
    case RESET_BANNER_ERROR: {
      draft.errors.bannerError = initialState.errors.bannerError;
      break;
    }
    case RESET_CANCEL_CONFIRMATION: {
      draft.otp.cancelConfirmation = initialState.otp.cancelConfirmation;
      break;
    }
    case RESET_CONFIRMATION: {
      draft.errors.serviceError.createOtp = false;
      draft.otp.confirmation = false;
      break;
    }
    // Resets state related to the SchedulePayment page (i.e. EditAmount, SelectDate, EditSource)
    case RESET_ONE_TIME_PAYMENT_STATE: {
      draft.errors = initialState.errors;
      draft.isInPaymentFlow = false;
      draft.otp.formErrors = initialState.otp.formErrors;
      draft.otp.forms = {
        account: initialState.otp.forms.account,
        amount: {
          ...initialState.otp.forms.amount,
          numberOfPayments: original(draft).otp.availableNumberOfPayments[0] || 1,
        },
        review: initialState.otp.forms.review,
      };
      draft.otp.scheduledDate = draft.otp.availableDraftDates && draft.otp.availableDraftDates[0] ?
        draft.otp.availableDraftDates[0] : '';
      draft.otp.payType = draft.otp.paymentLogic.canMakeMonthlyPayment ? 'Monthly' : 'Additional';
      draft.successes = initialState.successes;
      break;
    }
    case RESET_STEP: {
      draft.step = initialState.step;
      break;
    }
    case RESET_SWITCH_LOAN_STATE: {
      return initialState;
    }
    case RESET_USER_STATE: {
      return initialState;
    }
    case SET_BANNER_ERROR: {
      const { value } = payload;
      draft.errors.bannerError = value;
      break;
    }
    case SET_IS_AMOUNT_DRAWER_OPEN: {
      const { value } = payload;
      draft.otp.isAmountDrawerOpen = value;
      break;
    }
    case SET_IS_MODAL_OPEN: {
      const { key, value } = payload;
      draft.otp.isModalOpen[key] = value;
      break;
    }
    case UPDATE_AMOUNT: {
      const { amount, payType } = payload;
      draft.otp.forms.amount = amount;
      draft.otp.payType = payType;
      break;
    }
    case UPDATE_PAYMENT_HOME_INIT: {
      const { state } = payload;
      draft.isPaymentHomeInit = state;
      break;
    }
  }
}, initialState);

export default oneTimePaymentReducer;
